import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import type { PhotoItem } from '@/types/odoo';
import {
  clampView, DOUBLE_TAP_SCALE, IDENTITY_VIEW, pinchView, zoomAt, type Point, type View,
} from './lightboxView';

interface PhotoLightboxProps {
  /** `download` overrides what the download button saves (a rendered PDF page saves its PDF). */
  photos: (PhotoItem & { download?: { url: string; filename: string } })[];
  initialIndex: number;
  onClose: () => void;
}

const ZOOM_THRESHOLD = 1.1;
// Two taps within this time and distance are a double-tap.
const DOUBLE_TAP_MS = 300;
const DOUBLE_TAP_PX = 30;

export function PhotoLightbox({ photos, initialIndex, onClose }: PhotoLightboxProps) {
  const [currentIndex, setCurrentIndex] = useState(Math.max(0, Math.min(initialIndex, photos.length - 1)));
  const [view, setView] = useState<View>(IDENTITY_VIEW);
  const [isDragging, setIsDragging] = useState(false);

  const dialogRef = useRef<HTMLDivElement>(null);
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  // A one-finger touch that may still become a swipe or a tap.
  const touchStartRef = useRef<{ x: number; y: number; time: number } | null>(null);
  const lastTapRef = useRef<{ x: number; y: number; time: number } | null>(null);
  const pinchStartRef = useRef<{ view: View; mid: Point; dist: number } | null>(null);

  // The view as of the last change, read by the non-passive event handlers.
  // Updated at once (not on render) so two events in one frame build on each other.
  const viewRef = useRef(view);

  // Clamp to keep the image on screen, then show it. The image's laid-out size
  // comes from its computed style: unlike offsetWidth it keeps the fraction of
  // a pixel (which the zoom multiplies) and, unlike its bounding box, it ignores
  // the transform. offsetWidth is the fallback where there is no layout (tests).
  const applyView = useCallback((next: View) => {
    const img = imageRef.current;
    const box = imageContainerRef.current?.getBoundingClientRect();
    let clamped = next;
    if (img && box) {
      const style = getComputedStyle(img);
      const width = parseFloat(style.width);
      const height = parseFloat(style.height);
      clamped = clampView(
        next,
        {
          width: Number.isFinite(width) ? width : img.offsetWidth,
          height: Number.isFinite(height) ? height : img.offsetHeight,
        },
        { width: box.width, height: box.height },
      );
    }
    viewRef.current = clamped;
    setView(clamped);
  }, []);

  const goPrevRef = useRef<() => void>(() => {});
  const goNextRef = useRef<() => void>(() => {});

  // Mouse drag refs
  const mouseDragRef = useRef<{ startX: number; startY: number; startTx: number; startTy: number } | null>(null);

  // Touch drag (single finger pan when zoomed): the finger and the view at its start
  const touchDragRef = useRef<{ x: number; y: number; view: View } | null>(null);

  const current = photos[currentIndex];
  const total = photos.length;

  const isZoomed = view.scale > ZOOM_THRESHOLD;

  // Reset zoom and pan when photo changes
  const resetTransform = useCallback(() => {
    viewRef.current = IDENTITY_VIEW;
    setView(IDENTITY_VIEW);
  }, []);

  const goTo = useCallback((index: number) => {
    if (index < 0 || index >= total) return;
    setCurrentIndex(index);
    resetTransform();
  }, [total, resetTransform]);

  const goPrev = useCallback(() => {
    if (currentIndex > 0) {
      goTo(currentIndex - 1);
    } else {
      onClose();
    }
  }, [currentIndex, goTo, onClose]);

  const goNext = useCallback(() => {
    if (currentIndex < total - 1) {
      goTo(currentIndex + 1);
    } else {
      onClose();
    }
  }, [currentIndex, total, goTo, onClose]);

  goPrevRef.current = goPrev;
  goNextRef.current = goNext;

  // Body scroll lock
  useEffect(() => {
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = original;
    };
  }, []);

  // Focus trap -- focus dialog on mount
  useEffect(() => {
    dialogRef.current?.focus();
  }, []);

  // Keyboard navigation
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowLeft':
          goPrev();
          break;
        case 'ArrowRight':
          goNext();
          break;
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => { window.removeEventListener('keydown', handleKeyDown); };
  }, [onClose, goPrev, goNext]);

  // Scroll wheel zoom (zoom toward cursor)
  useEffect(() => {
    const container = imageContainerRef.current;
    if (!container) return;

    function handleWheel(e: WheelEvent) {
      e.preventDefault();

      const rect = container!.getBoundingClientRect();
      const cursor = { x: e.clientX - rect.left - rect.width / 2, y: e.clientY - rect.top - rect.height / 2 };
      const prev = viewRef.current;
      applyView(zoomAt(prev, cursor, prev.scale * (1 - e.deltaY * 0.001)));
    }

    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      container.removeEventListener('wheel', handleWheel);
    };
  }, [applyView]);

  // Mouse drag cleanup ref — stores removeListeners fn for unmount safety
  const cleanupMouseDragRef = useRef<(() => void) | null>(null);

  // Cleanup global mouse listeners on unmount (safety net for mid-drag unmount)
  useEffect(() => {
    return () => {
      cleanupMouseDragRef.current?.();
      cleanupMouseDragRef.current = null;
      mouseDragRef.current = null;
    };
  }, []);

  // Touch handlers for swipe, pinch-to-zoom, pan and double-tap (non-passive for preventDefault)
  useEffect(() => {
    const container = imageContainerRef.current;
    if (!container) return;

    // A touch point in container pixels, from the container centre.
    function toPoint(clientX: number, clientY: number): Point {
      const rect = container!.getBoundingClientRect();
      return { x: clientX - rect.left - rect.width / 2, y: clientY - rect.top - rect.height / 2 };
    }

    function getTouchDist(touches: TouchList): number {
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      return Math.sqrt(dx * dx + dy * dy);
    }

    function getTouchMidpoint(touches: TouchList): Point {
      return toPoint((touches[0].clientX + touches[1].clientX) / 2, (touches[0].clientY + touches[1].clientY) / 2);
    }

    // Pan with one finger from the view as it is now (a new touch, or the finger left after a pinch).
    function startDrag(touch: Touch) {
      touchDragRef.current = viewRef.current.scale > ZOOM_THRESHOLD
        ? { x: touch.clientX, y: touch.clientY, view: viewRef.current }
        : null;
    }

    function handleTouchStart(e: TouchEvent) {
      if (e.touches.length === 1) {
        const touch = e.touches[0];
        // Gesture times are the events' own, so a busy page does not turn a quick swipe into a slow one.
        touchStartRef.current = { x: touch.clientX, y: touch.clientY, time: e.timeStamp };
        pinchStartRef.current = null;
        startDrag(touch);
      } else if (e.touches.length === 2) {
        // A pinch is never a swipe or a tap.
        touchStartRef.current = null;
        touchDragRef.current = null;
        lastTapRef.current = null;
        pinchStartRef.current = { view: viewRef.current, mid: getTouchMidpoint(e.touches), dist: getTouchDist(e.touches) };
      }
    }

    function handleTouchMove(e: TouchEvent) {
      const pinch = pinchStartRef.current;
      if (e.touches.length === 2 && pinch) {
        e.preventDefault();
        applyView(pinchView(pinch.view, pinch.mid, pinch.dist, getTouchMidpoint(e.touches), getTouchDist(e.touches)));
      } else if (e.touches.length === 1 && touchDragRef.current) {
        e.preventDefault();
        const drag = touchDragRef.current;
        const touch = e.touches[0];
        applyView({
          scale: drag.view.scale,
          x: drag.view.x + touch.clientX - drag.x,
          y: drag.view.y + touch.clientY - drag.y,
        });
      }
    }

    function handleTouchEnd(e: TouchEvent) {
      if (e.touches.length === 1) {
        // One finger lifted from a pinch: the other one pans on from here.
        pinchStartRef.current = null;
        startDrag(e.touches[0]);
        return;
      }
      if (e.touches.length > 0) return;

      const start = touchStartRef.current;
      const end = e.changedTouches[0];
      if (start && end) {
        const dx = end.clientX - start.x;
        const dy = end.clientY - start.y;
        const now = e.timeStamp;
        const dt = now - start.time;
        const absDx = Math.abs(dx);
        const absDy = Math.abs(dy);

        if (absDx < 10 && absDy < 10 && dt < DOUBLE_TAP_MS) {
          const last = lastTapRef.current;
          if (last && now - last.time < DOUBLE_TAP_MS
            && Math.abs(end.clientX - last.x) < DOUBLE_TAP_PX && Math.abs(end.clientY - last.y) < DOUBLE_TAP_PX) {
            // Double-tap: zoom in at the tap, or back out. preventDefault keeps the
            // browser from also sending its own double-click, which resets the zoom.
            e.preventDefault();
            lastTapRef.current = null;
            if (viewRef.current.scale > ZOOM_THRESHOLD) {
              applyView(IDENTITY_VIEW);
            } else {
              applyView(zoomAt(viewRef.current, toPoint(end.clientX, end.clientY), DOUBLE_TAP_SCALE));
            }
          } else {
            lastTapRef.current = { x: end.clientX, y: end.clientY, time: now };
          }
        } else if (viewRef.current.scale <= ZOOM_THRESHOLD && absDx > 50 && absDx > absDy && dt < 300) {
          // Swipe to navigate (single finger, not zoomed)
          if (dx > 0) {
            goPrevRef.current();
          } else {
            goNextRef.current();
          }
        }
      }
      // Pinched out below full size: settle back at full size.
      if (viewRef.current.scale < 1) applyView(IDENTITY_VIEW);
      touchStartRef.current = null;
      touchDragRef.current = null;
      pinchStartRef.current = null;
    }

    container.addEventListener('touchstart', handleTouchStart, { passive: false });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd, { passive: false });
    container.addEventListener('touchcancel', handleTouchEnd, { passive: false });
    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
      container.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, [applyView]);

  // If photos array is empty, close and bail out
  useEffect(() => {
    if (!photos.length) {
      onClose();
    }
  }, [photos.length, onClose]);

  if (!photos.length) {
    return null;
  }

  // Double-click resets zoom
  function handleDoubleClick(e: React.MouseEvent) {
    e.stopPropagation();
    resetTransform();
  }

  // Mouse down on image starts drag when zoomed — registers global listeners on demand
  function handleImageMouseDown(e: React.MouseEvent) {
    e.stopPropagation();
    if (viewRef.current.scale > ZOOM_THRESHOLD) {
      e.preventDefault();
      mouseDragRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        startTx: viewRef.current.x,
        startTy: viewRef.current.y,
      };
      setIsDragging(true);

      const handleMouseMove = (ev: MouseEvent) => {
        if (!mouseDragRef.current) return;
        ev.preventDefault();
        const dx = ev.clientX - mouseDragRef.current.startX;
        const dy = ev.clientY - mouseDragRef.current.startY;
        applyView({ scale: viewRef.current.scale, x: mouseDragRef.current.startTx + dx, y: mouseDragRef.current.startTy + dy });
      };

      const handleMouseUp = () => {
        mouseDragRef.current = null;
        setIsDragging(false);
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
        cleanupMouseDragRef.current = null;
      };

      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);

      // Store cleanup fn so unmount can remove listeners if drag is in progress
      cleanupMouseDragRef.current = () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }

  // Overlay click closes (but not image click)
  function handleOverlayClick(e: React.MouseEvent) {
    if (e.target === e.currentTarget) {
      onClose();
    }
  }

  function handleDownload() {
    const a = document.createElement('a');
    a.href = current.download?.url ?? current.url;
    a.download = current.download?.filename ?? current.filename;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  const hasTransform = view.scale !== 1 || view.x !== 0 || view.y !== 0;
  const imageTransform = hasTransform
    ? `translate(${view.x}px, ${view.y}px) scale(${view.scale})`
    : undefined;

  const cursorClass = isZoomed
    ? isDragging ? 'cursor-grabbing' : 'cursor-grab'
    : '';

  return createPortal(
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label="Foto weergave"
      tabIndex={-1}
      className="fixed inset-0 z-[61] flex flex-col items-center justify-center outline-none"
    >
      {/* Dark overlay */}
      <div
        className="absolute inset-0 bg-black/90"
        onClick={handleOverlayClick}
        aria-hidden="true"
      />

      {/* Top bar: counter + close */}
      <div className="relative z-10 w-full flex items-center justify-between px-4 py-3 sm:px-6">
        <span className="text-white/80 text-sm font-medium">
          {currentIndex + 1} / {total}
        </span>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleDownload}
            className="text-white/80 hover:text-white transition-colors p-2"
            aria-label="Foto downloaden"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3" />
            </svg>
          </button>
          <button
            type="button"
            onClick={onClose}
            className="text-white/80 hover:text-white transition-colors p-2"
            aria-label="Sluiten"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Main image area */}
      <div
        ref={imageContainerRef}
        className="relative z-10 flex-1 w-full flex items-center justify-center min-h-0 px-4 sm:px-16 overflow-hidden touch-none"
        onClick={handleOverlayClick}
      >
        {/* Left arrow */}
        {total > 1 && (
          <button
            type="button"
            onClick={goPrev}
            className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 z-20 text-white/60 hover:text-white transition-colors p-2 sm:p-3"
            aria-label="Vorige foto"
          >
            <svg className="w-8 h-8 sm:w-10 sm:h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        )}

        {/* Image */}
        <img
          ref={imageRef}
          src={current.url}
          alt={current.filename}
          className={`max-w-full max-h-full object-contain select-none origin-center ${cursorClass}`}
          style={imageTransform ? { transform: imageTransform } : undefined}
          draggable={false}
          onMouseDown={handleImageMouseDown}
          onDoubleClick={handleDoubleClick}
          onClick={(e) => { e.stopPropagation(); }}
        />

        {/* Right arrow */}
        {total > 1 && (
          <button
            type="button"
            onClick={goNext}
            className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 z-20 text-white/60 hover:text-white transition-colors p-2 sm:p-3"
            aria-label="Volgende foto"
          >
            <svg className="w-8 h-8 sm:w-10 sm:h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        )}
      </div>

      {/* Bottom bar: filename */}
      <div className="relative z-10 w-full px-4 py-3 sm:px-6 text-center">
        <p className="text-white/70 text-xs sm:text-sm truncate">
          {current.filename}
        </p>
      </div>
    </div>,
    document.body,
  );
}
